import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MusicalNoteIcon, TrashIcon, PencilIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { IMusicEditing, TaskStatus, MusicEditingTypeLabels, getLabelsByLocale, Locale } from '@packages/global';
import MusicTitleEditor from '../MusicGeneration/MusicTitleEditor';
import { musicEditingApi } from '../../services/user/musicEditing.api';
import AudioPlayer from '../common/AudioPlayer';

interface Props {
  item: IMusicEditing;
  onDelete: (id: string) => void;
  onReuse: (item: IMusicEditing) => void;
  onTitleChange?: (id: string, title: string) => void;
}

const STATUS_STYLES: Record<string, string> = {
  [TaskStatus.PENDING]: 'bg-bg-elevated text-gray-300 border border-border-subtle',
  [TaskStatus.SUBMITTED]: 'bg-warning/20 text-warning border border-warning/30',
  [TaskStatus.PROCESSING]: 'bg-info/20 text-info border border-info/30',
  [TaskStatus.COMPLETED]: 'bg-success/20 text-success border border-success/30',
  [TaskStatus.FAILED]: 'bg-error/20 text-error border border-error/30',
};

async function downloadAudio(url: string, filename: string) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(objectUrl);
    document.body.removeChild(a);
  } catch (error) {
    console.error('下载失败:', error);
  }
}

export default function MusicEditCard({ item, onDelete, onReuse, onTitleChange }: Props) {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const musicEditingTypeLabels = getLabelsByLocale(MusicEditingTypeLabels, i18n.language as Locale);
  const isProcessing = [TaskStatus.PENDING, TaskStatus.SUBMITTED, TaskStatus.PROCESSING].includes(item.status as TaskStatus);
  const isSeparate = item.editingType === 'separate';
  const isSongCover = item.editingType === 'song_cover';
  const hasSecondaryTrack = isSeparate || isSongCover;
  const [localTitle, setLocalTitle] = useState<string | undefined>(item.title);

  function formatDate(date: string | Date) {
    return new Date(date).toLocaleString(i18n.language, {
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  }

  const handleSaveTitle = async (id: string, title: string) => {
    await musicEditingApi.updateTitle(id, title);
    setLocalTitle(title || undefined);
    onTitleChange?.(id, title);
  };

  return (
    <div className="bg-bg-surface rounded-xl border border-border-subtle hover:border-primary/50 hover:shadow-[0_0_16px_rgba(255,92,0,0.12)] transition-all duration-300 overflow-hidden flex flex-col">
      {/* 头部 */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center flex-shrink-0">
            <MusicalNoteIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <MusicTitleEditor
              id={item.id}
              title={localTitle}
              fallback={item.id.slice(0, 8)}
              onSave={handleSaveTitle}
            />
            <p className="text-xs text-gray-500 truncate">
              {musicEditingTypeLabels[item.editingType] ?? item.editingType}
            </p>
          </div>
        </div>
        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0 backdrop-blur-sm ${STATUS_STYLES[item.status] ?? ''}`}>
          {t(`generation:status.${item.status}`)}
        </span>
      </div>

      {/* 源音频播放器 */}
      {item.srcAudioUrl && (
        <div className="px-4 pb-2">
          <p className="text-xs text-gray-500 mb-1">{t('generation:musicEdit.card.sourceAudio')}</p>
          <AudioPlayer src={item.srcAudioUrl} />
        </div>
      )}

      {/* 处理中动画 */}
      {isProcessing && (
        <div className="px-4 pb-2 flex items-center gap-2">
          <div className="flex gap-0.5">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className="w-1 bg-primary rounded-full animate-pulse"
                style={{ height: `${8 + i * 4}px`, animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
          <span className="text-xs text-gray-400">
            {item.status === TaskStatus.PENDING
              ? t('generation:musicEdit.card.waiting')
              : t('generation:musicEdit.card.processing')}
          </span>
        </div>
      )}

      {/* 结果音频 */}
      {item.status === TaskStatus.COMPLETED && item.resultAudioUrl && (
        <div className="px-4 pb-2">
          <p className="text-xs text-gray-500 mb-1">
            {isSeparate
              ? t('generation:musicEdit.card.vocalTrack')
              : isSongCover
                ? t('generation:musicEdit.card.coverResult')
                : t('generation:musicEdit.card.resultAudio')}
          </p>
          <AudioPlayer src={item.resultAudioUrl} />
        </div>
      )}

      {/* 第二条音轨：separate=伴奏，song_cover=干声 */}
      {item.status === TaskStatus.COMPLETED && hasSecondaryTrack && item.secondaryResultAudioUrl && (
        <div className="px-4 pb-2">
          <p className="text-xs text-gray-500 mb-1">
            {isSeparate
              ? t('generation:musicEdit.card.accompanimentTrack')
              : t('generation:musicEdit.card.dryVocalTrack')}
          </p>
          <AudioPlayer src={item.secondaryResultAudioUrl} />
        </div>
      )}

      {/* 失败信息 */}
      {item.status === TaskStatus.FAILED && item.resultMessage && (
        <div className="px-4 pb-2">
          <p className="text-xs text-red-400 line-clamp-2" title={item.resultMessage}>{item.resultMessage}</p>
        </div>
      )}

      {/* 底部 */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle mt-auto">
        <span className="text-xs text-gray-500">{formatDate(item.createdAt)}</span>
        <div className="flex gap-1">
          {item.status === TaskStatus.COMPLETED && item.resultAudioUrl && (
            <button
              onClick={() => downloadAudio(item.resultAudioUrl!, `music-edit-${item.id}.mp3`)}
              className="p-1.5 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white transition-all cursor-pointer"
              title={isSeparate
                ? t('generation:musicEdit.card.downloadVocalTrack')
                : t('generation:musicEdit.card.downloadResultAudio')}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
          )}
          {item.status === TaskStatus.COMPLETED && hasSecondaryTrack && item.secondaryResultAudioUrl && (
            <button
              onClick={() => downloadAudio(item.secondaryResultAudioUrl!, `music-${isSeparate ? 'separate-accompaniment' : 'cover-dry-vocal'}-${item.id}.mp3`)}
              className="p-1.5 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white transition-all cursor-pointer"
              title={isSeparate
                ? t('generation:musicEdit.card.downloadAccompanimentTrack')
                : t('generation:musicEdit.card.downloadDryVocalTrack')}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => onReuse(item)}
            className="p-1.5 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white transition-all cursor-pointer"
            title={t('generation:musicEdit.card.reuseParams')}
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded-lg bg-bg-elevated hover:bg-red-500/80 text-gray-300 hover:text-white transition-all cursor-pointer"
            title={t('common:delete')}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
